import { useState } from 'react';
import axios from 'axios';
import { ChevronRightIcon, ChevronLeftIcon } from '@radix-ui/react-icons';
import { InfoInformationCircleIcon, ReceiptIcon } from '@qitech/icons';

import { ErrorPage } from '../../../ErrorPage';
import { SocialSecurityLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import { SocialSecurityDocumentViewer } from '../components/DocumentViewer';
import * as S from './styles';

interface FolderMode {
    onBack: () => void;
    currentDecision?: string;
    submitDecision: (decision: 'accepted' | 'rejected') => Promise<void>;
    refreshDetail?: () => Promise<any>;
}

interface ResumeOperationProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
    folderMode?: FolderMode;
}

const formatDate = (inputDate: string): string => {
    const [year, month, day] = inputDate.split('-');
    return `${day}/${month}/${year}`;
};

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

const formatPercent = (value: number): string =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

const formatInstallments = (count?: number, installmentValue?: number | null): string => {
    if (count == null) return '-';
    if (installmentValue == null) return `${count}x`;
    return `${count}x ${formatCurrency(installmentValue)}`;
};

export const SocialSecurityResumeOperation = ({
    signData,
    setSignerStep,
    setSignData,
    folderMode,
}: ResumeOperationProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);
    const text = resolveColor('text', clientThemeSettings);
    const initialError = { has_error: false, status: 0 };

    const isFolderMode = !!folderMode;
    const folderDecided = folderMode?.currentDecision;
    const folderAccepted = folderDecided === 'accepted';
    const folderRejected = folderDecided === 'declined' || folderDecided === 'rejected';

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(false);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [isAwaitingUrls, setIsAwaitingUrls] = useState(false);
    const [viewerDoc, setViewerDoc] = useState<{
        url: string | null;
        title: string;
        extract?: (data: any) => string | null | undefined;
    } | null>(null);

    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([]);
    const checkboxModal = useAnimatedModal();

    const op = signData?.credit_operation_data;
    const documentUrl = signData?.document_url;
    const hasDocumentUrlKey = !!signData && 'document_url' in signData;

    const hasPendingDocumentUrl = (data: any): boolean =>
        !!data && 'document_url' in data && data.document_url === null;

    const fetchFreshStepData = async (): Promise<any | null> => {
        if (isFolderMode) {
            return folderMode?.refreshDetail ? await folderMode.refreshDetail() : null;
        }
        const config = { headers: { Authorization: signData.signer_token } };
        const r = await axios.get('/signer', config);
        return r.data;
    };

    const waitForDocumentUrl = async (): Promise<{ ok: boolean; data: any }> => {
        if (isFolderMode && !folderMode?.refreshDetail) return { ok: true, data: signData };
        if (!hasPendingDocumentUrl(signData)) return { ok: true, data: signData };
        setIsAwaitingUrls(true);
        const startedAt = Date.now();
        let merged: any = signData;
        try {
            while (Date.now() - startedAt < 120000) {
                await new Promise((r) => setTimeout(r, 15000));
                try {
                    const fresh = await fetchFreshStepData();
                    if (!fresh) continue;
                    merged = { ...merged, ...fresh };
                    setSignData(merged);
                    if (!hasPendingDocumentUrl(fresh)) return { ok: true, data: merged };
                } catch {}
            }
            return { ok: false, data: merged };
        } finally {
            setIsAwaitingUrls(false);
        }
    };

    const openDocumentViewerWithPolling = (
        url: string | null | undefined,
        title: string,
        extract: (data: any) => string | null | undefined,
    ) => {
        setViewerDoc({ url: url ?? null, title, extract });
    };

    const pollForDocumentUrl = async (
        extract: (data: any) => string | null | undefined,
    ): Promise<string | null | undefined> => {
        if (isFolderMode && !folderMode?.refreshDetail) return extract(signData);
        const fresh = await fetchFreshStepData();
        if (!fresh) return extract(signData);
        setSignData({ ...signData, ...fresh });
        return extract(fresh);
    };

    const handleFolderDecision = async (decision: 'accepted' | 'rejected') => {
        if (loading || !folderMode) return;
        setLoading(true);
        try {
            await folderMode.submitDecision(decision);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (hasMadeRequest || isAwaitingUrls) return;

        const urlsResult = await waitForDocumentUrl();
        if (!urlsResult.ok) {
            setError({ has_error: true, status: 500 });
            return;
        }

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload: any = {
            step: 'resume_operation',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((error) => {
                const status = [401, 403, 409].includes(error.response?.status)
                    ? error.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleButtonClick = () => {
        if (!hasScrolledToEnd) {
            scrollToBottom();
            return;
        }
        if (!isChecked) {
            checkboxModal.open();
            return;
        }
        handleSubmit();
    };

    const ButtonSpinner = ({ color = '#ffffff' }: { color?: string }) => (
        <>
            <style>{`@keyframes qiBtnSpin{to{transform:rotate(360deg);}}`}</style>
            <span
                aria-hidden
                style={{
                    display: 'inline-block',
                    width: 16,
                    height: 16,
                    border: `2px solid ${color}55`,
                    borderTopColor: color,
                    borderRadius: '50%',
                    animation: 'qiBtnSpin 0.8s linear infinite',
                }}
            />
        </>
    );

    const awaitingLabel = 'Preparando documentos...';

    if (loading) return <SocialSecurityLoading />;

    if (viewerDoc) {
        return (
            <SocialSecurityDocumentViewer
                url={viewerDoc.url}
                title={viewerDoc.title}
                onBack={() => setViewerDoc(null)}
                pollForUrl={viewerDoc.extract ? () => pollForDocumentUrl(viewerDoc.extract!) : undefined}
            />
        );
    }

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            {isFolderMode && (
                <S.FolderBackBar>
                    <S.FolderBackButton $color={primary} type="button" onClick={folderMode!.onBack}>
                        <ChevronLeftIcon width={18} height={18} />
                        Voltar para propostas
                    </S.FolderBackButton>
                </S.FolderBackBar>
            )}

            <S.ScrollContent>
                <S.HeaderBlock>
                    <S.Title $color={primary}>Resumo do seu empréstimo</S.Title>
                    <S.Subtitle>
                        Revise as condições da sua proposta de crédito antes de assinar.
                    </S.Subtitle>
                </S.HeaderBlock>

                <S.DataCardContainer $color={primary}>
                    <S.CardHeader $color={primary}>
                        <S.CardHeaderIcon $color={primary}>
                            <ReceiptIcon size={22} />
                        </S.CardHeaderIcon>
                        <S.CardHeaderTitle $color={primary}>Resumo da operação</S.CardHeaderTitle>
                    </S.CardHeader>

                    <S.DataRow>
                        <S.RowLabel>Valor total do empréstimo</S.RowLabel>
                        <S.RowValue $color={primary}>{formatCurrency(op?.issue_amount ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>IOF</S.RowLabel>
                        <S.RowValue $color={primary}>{formatCurrency(op?.iof ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel $bold>Valor líquido</S.RowLabel>
                        <S.RowValue $color={primary} $highlight>
                            {formatCurrency(op?.disbursed_amount ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>CET (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>{formatPercent(op?.monthly_cet ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Taxa de juros (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>{formatPercent(op?.monthly_interest_rate ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Parcelas mensais</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {formatInstallments(
                                op?.number_of_installments,
                                op?.installment_face_value ?? op?.installment_amount,
                            )}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Data da primeira parcela</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {op?.first_due_date ? formatDate(op.first_due_date) : '-'}
                        </S.RowValue>
                    </S.DataRow>
                </S.DataCardContainer>

                {hasDocumentUrlKey && (
                    <S.InfoCallout $color={primary}>
                        <S.IconWrapper $color={primary}>
                            <ReceiptIcon size={22} />
                        </S.IconWrapper>
                        <S.InfoText $color={primary}>
                            Para mais informações,{' '}
                            <a
                                href={documentUrl || '#'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    openDocumentViewerWithPolling(
                                        documentUrl,
                                        'Contrato de empréstimo',
                                        (d) => d?.document_url,
                                    );
                                }}
                            >
                                consulte o contrato da operação de empréstimo.
                            </a>
                        </S.InfoText>
                    </S.InfoCallout>
                )}

                <S.InfoCallout $color={primary} $neutral>
                    <S.IconWrapper $color={primary} $neutral>
                        <InfoInformationCircleIcon size={22} />
                    </S.IconWrapper>
                    <S.InfoText $color={primary}>
                        As parcelas deste empréstimo são descontadas diretamente do seu{' '}
                        <strong>benefício do INSS</strong>, dentro da sua margem consignável.
                    </S.InfoText>
                </S.InfoCallout>

                {!isFolderMode && (
                    <S.CheckboxWrapper>
                        <CheckboxField
                            id="resume-operation-terms"
                            checked={isChecked}
                            onChange={(e) => setIsChecked(e.target.checked)}
                            checkboxColor={primary}
                            textColor={text}
                        >
                            <strong style={{ color: primary }}>Confirmo que li e estou de acordo</strong> com as condições de contratação do meu empréstimo.
                        </CheckboxField>
                    </S.CheckboxWrapper>
                )}

                <div ref={contentEndRef} />
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.Footer>
                {isFolderMode ? (
                    <>
                        {(folderAccepted || folderRejected) && (
                            <S.FolderCurrentDecision $color={primary}>
                                {folderAccepted
                                    ? 'Você aceitou esta proposta. Você pode alterar abaixo.'
                                    : 'Você recusou esta proposta. Você pode alterar abaixo.'}
                            </S.FolderCurrentDecision>
                        )}
                        <S.FolderDecisionRow>
                            <S.OutlineButton
                                $color={primary}
                                $disabled={folderRejected}
                                onClick={folderRejected ? undefined : () => handleFolderDecision('rejected')}
                            >
                                Recusar
                            </S.OutlineButton>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={folderAccepted}
                                onClick={folderAccepted ? undefined : () => handleFolderDecision('accepted')}
                            >
                                Aceitar
                            </S.SubmitButton>
                        </S.FolderDecisionRow>
                    </>
                ) : (
                    <S.SubmitButton
                        $backgroundColor={primary}
                        $disabled={!isChecked || isAwaitingUrls}
                        onClick={handleButtonClick}
                    >
                        {isAwaitingUrls ? (
                            <>
                                <ButtonSpinner />
                                {awaitingLabel}
                            </>
                        ) : (
                            <>
                                Aceitar termos do contrato
                                <ChevronRightIcon width={24} height={24} />
                            </>
                        )}
                    </S.SubmitButton>
                )}
                <PoweredByQITech />
            </S.Footer>

            {checkboxModal.show && (
                <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                    <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.SheetHandle />
                        <S.ModalTitle $color={primary}>Confirmação pendente</S.ModalTitle>
                        <S.ModalText style={{ textAlign: 'start' }}>
                            Para continuar, você precisa marcar a seguinte caixa acima:
                            <ul>
                                <li><strong>Aceite das condições do empréstimo</strong></li>
                            </ul>
                        </S.ModalText>
                        <S.ModalButtonRow>
                            <S.SubmitButton $backgroundColor={primary} $disabled={false} onClick={checkboxModal.close}>
                                Entendi
                                <ChevronRightIcon width={24} height={24} />
                            </S.SubmitButton>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </S.PageWrapper>
    );
};
